/**
 * Global error handling utilities
 */

/**
 * Initialize global error handlers
 */
export const initializeErrorHandling = (): void => {
  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    console.error("Unhandled promise rejection:", event.reason);

    // Prevent the default browser behavior (logging to console)
    event.preventDefault();

    // You could send this to an error reporting service
    reportError("Unhandled Promise Rejection", event.reason);
  });

  // Handle general JavaScript errors
  window.addEventListener("error", (event) => {
    console.error("Global error:", event.error);
    reportError("JavaScript Error", event.error);
  });

  // Handle resource loading errors
  window.addEventListener(
    "error",
    (event) => {
      if (event.target && event.target !== window) {
        const target = event.target as HTMLElement;
        if (target.tagName === "IMG") {
          console.warn(
            "Image failed to load:",
            (target as HTMLImageElement).src
          );
        } else if (target.tagName === "SCRIPT") {
          console.error(
            "Script failed to load:",
            (target as HTMLScriptElement).src
          );
        } else if (target.tagName === "LINK") {
          console.error(
            "Stylesheet failed to load:",
            (target as HTMLLinkElement).href
          );
        }
      }
    },
    true
  ); // Use capture phase for resource errors
};

/**
 * Report error to monitoring service (placeholder)
 */
const reportError = (type: string, error: any): void => {
  // In a real application, you would send this to an error reporting service
  // like Sentry, LogRocket, or similar
  const errorInfo = {
    type,
    message: error?.message || String(error),
    stack: error?.stack,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  // For now, just log to console in development
  if (process.env.NODE_ENV === "development") {
    console.group("🚨 Error Report");
    console.table(errorInfo);
    console.groupEnd();
  }

  // In production, you would send this to your error reporting service
  // Example: Sentry.captureException(error, { extra: errorInfo });
};

/**
 * Safely execute async operations with error handling
 */
export const safeAsync = async <T>(
  operation: () => Promise<T>,
  fallback: T,
  errorMessage?: string
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    console.error(errorMessage || "Async operation failed:", error);
    reportError("Async Operation Error", error);
    return fallback;
  }
};

/**
 * Retry mechanism for failed operations
 */
export const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      console.warn(
        `Operation failed (attempt ${attempt}/${maxRetries}):`,
        error
      );

      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
      }
    }
  }

  throw lastError;
};
